/**
 * "Complete your look" — recommendations for everything in the cart.
 *
 * Shopify's `recommendations` object only exists inside a response from the
 * product recommendations route, so the panel has to be fetched rather than
 * rendered with the drawer. The drawer re-derives the cart's product ids on
 * every re-render, so the panel follows the cart.
 *
 * The endpoint takes a single `product_id`, so covering a whole cart means one
 * request per cart product and merging the replies into one carousel. The merge
 * is round robin — first pick from each source, then second pick from each —
 * so a two-item cart contributes to the panel evenly instead of the first line
 * filling every slot.
 *
 * Named for what it is rather than for the panel it loads: the panel markup is
 * snippets/component-cart-recommendations.liquid, and <cart-upsell> is emitted
 * by the drawer, not by that snippet.
 *
 * The theme's shared <product-recommendations> fetches on intersection, which
 * is wrong here: the drawer is translated off-screen until it opens, so the
 * observer either never fires or fires for a panel nobody can see. This element
 * fetches when the drawer opens instead.
 *
 * Responses are cached per URL for the life of the page, because the drawer
 * re-renders on every cart mutation and the recommendations for a given product
 * do not change between two clicks of a quantity stepper.
 *
 * @extends HTMLElement
 */
const responseCache = new Map();

/**
 * A cached panel is stored as markup and re-parsed per use, because mergeSlides
 * moves its slides into another document and would otherwise empty the cache
 * entry the first time it ran.
 *
 * @param {string} markup
 * @returns {Element|null}
 */
function parsePanel(markup) {
  return new DOMParser().parseFromString(markup, 'text/html').body.firstElementChild;
}

/* Shopify's algorithmic related products first; the merchant's complementary
   lists only if the related pass found nothing at all for the whole cart.

   Order matters more than it looks. Preference is resolved across the cart, not
   per product: the first intent that yields any slide wins outright and the
   other never runs. So complementary is reached only while Shopify's
   recommendation index has nothing to say about anything in the cart — once
   that index has data, related answers for most products and the curated lists
   go unused. That is the intended behaviour here; swap the two entries back to
   let curation win instead. */
const INTENTS = ['related', 'complementary'];

export class CartUpsell extends HTMLElement {
  constructor() {
    super();
    this.onDrawerOpen = this.onDrawerOpen.bind(this);
  }

  connectedCallback() {
    this.drawer = this.closest('cart-drawer');

    if (this.drawer?.isOpen) this.load();
    else this.drawer?.addEventListener('cart-drawer:open', this.onDrawerOpen);
  }

  disconnectedCallback() {
    this.drawer?.removeEventListener('cart-drawer:open', this.onDrawerOpen);
    this.controller?.abort();
  }

  onDrawerOpen() {
    this.load();
  }

  /** @returns {string[]} Cart product ids, in cart order. */
  get cartProductIds() {
    return (this.dataset.cartProductIds || '')
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);
  }

  /**
   * The clubs in the cart, as `shop_by_team` handles. Empty when nothing in the
   * cart belongs to a club, which leaves the panel unfiltered.
   *
   * @returns {Set<string>}
   */
  get cartTeamHandles() {
    return new Set(
      (this.dataset.cartTeamHandles || '')
        .split(',')
        .map((handle) => handle.trim())
        .filter(Boolean),
    );
  }

  /** @returns {string[]} One collection URL per cart club, each answering for its own club. */
  get teamCollectionUrls() {
    return (this.dataset.teamCollectionUrls || '')
      .split(',')
      .map((url) => url.trim())
      .filter(Boolean);
  }

  /**
   * How many products the panel shows, and how many each request asks for.
   *
   * Clamped to the 1–10 the endpoint documents. The theme setting behind it is
   * a 2–10 range, so the editor cannot produce anything outside that — but
   * settings_data.json is hand-edited often enough on this project that the
   * guarantee is worth making here rather than assuming upstream.
   *
   * @returns {number}
   */
  get limit() {
    return Math.min(Math.max(parseInt(this.dataset.limit, 10) || 6, 1), 10);
  }

  /**
   * @param {string} productId
   * @param {string} intent
   * @returns {string}
   */
  urlFor(productId, intent) {
    const url = new URL(this.dataset.endpoint, window.location.origin);
    url.searchParams.set('section_id', this.dataset.sectionId);
    url.searchParams.set('product_id', productId);
    url.searchParams.set('limit', String(this.limit));
    url.searchParams.set('intent', intent);
    return url.href;
  }

  /**
   * @param {string} collectionUrl
   * @returns {string}
   */
  teamUrlFor(collectionUrl) {
    const url = new URL(collectionUrl, window.location.origin);
    url.searchParams.set('section_id', this.dataset.teamSectionId);
    url.searchParams.set('sort_by', 'best-selling');
    /* Unread by the section. The ranking depends on what is in the cart, and
       the response cache is keyed by URL — without this, adding a jersey after
       a cap would be answered with the ranking built for the cap. */
    url.searchParams.set('cart', this.dataset.cartProductIds);
    return url.href;
  }

  /**
   * Fetches one product's panel and returns it parsed, or null.
   *
   * **Only answers that found something are cached.** Shopify computes product
   * recommendations asynchronously and replies with an empty list for a product
   * it has not got to yet, filling it in on a later request. Caching that empty
   * reply froze the panel shut for the rest of the page's life, which is the
   * "it only shows up after I reload" — a reload was just a fresh cache. The
   * same applies to a transient non-OK response.
   *
   * A miss costs one request on the next cart change. A cached miss costs the
   * upsell entirely, so the trade only runs one way.
   *
   * @param {string} url
   * @returns {Promise<Element|null>}
   */
  async fetchPanel(url) {
    const cached = responseCache.get(url);
    if (cached) return parsePanel(cached);

    const response = await fetch(url, { signal: this.controller.signal });
    if (!response.ok) return null;

    const panel = new DOMParser()
      .parseFromString(await response.text(), 'text/html')
      .querySelector('.cart-drawer__recommendations-inner');

    /* The section renders the wrapper and nothing inside it when that product
       has no recommendations for this intent. */
    if (!panel || panel.children.length === 0) return null;

    responseCache.set(url, panel.outerHTML);
    return parsePanel(panel.outerHTML);
  }

  /**
   * Takes one slide from each source in turn, then puts in-stock slides ahead
   * of sold-out ones and cuts the result to the panel size.
   *
   * Skips anything already in the cart — a product the shopper has bought is
   * not an upsell, and asking about product A can easily return product B
   * sitting on the next line — and anything already picked, since two cart
   * items often recommend the same thing.
   *
   * The stock pass runs over the whole merge, not per source, so one club's
   * sold-out jersey cannot take a slot from another club's in-stock cap. It is
   * a stable partition: within each half, the round-robin order stands.
   *
   * When the cart holds club products, also skips anything from another club,
   * or from no club. The club section only renders its own club's products, so
   * this is a guard rather than the filter itself.
   *
   * @param {Element[]} panels One per source, in cart order
   * @returns {Element[]} Slides for the merged carousel
   */
  mergeSlides(panels) {
    const slideSets = panels.map((panel) => Array.from(panel.querySelectorAll('.scroll-carousel__slide')));
    const teams = this.cartTeamHandles;
    const excluded = new Set(this.cartProductIds);
    const merged = [];
    const longest = Math.max(0, ...slideSets.map((slides) => slides.length));

    for (let round = 0; round < longest; round += 1) {
      for (const slides of slideSets) {
        const slide = slides[round];
        const productId = slide?.dataset.productId;
        if (!slide || !productId || excluded.has(productId)) continue;
        if (teams.size > 0 && !teams.has(slide.dataset.teamHandle)) continue;

        excluded.add(productId);
        merged.push(slide);
      }
    }

    const inStock = merged.filter((slide) => slide.dataset.available === 'true');
    const soldOut = merged.filter((slide) => slide.dataset.available !== 'true');
    return [...inStock, ...soldOut].slice(0, this.limit);
  }

  /**
   * One panel per club in the cart, each already ranked for this cart by
   * sections/cart-team-products.
   *
   * @returns {Promise<Element[]>}
   */
  async fetchTeamPanels() {
    const panels = await Promise.all(this.teamCollectionUrls.map((url) => this.fetchPanel(this.teamUrlFor(url))));
    return panels.filter(Boolean);
  }

  /**
   * Shopify's own suggestions, for a cart with no club products in it: the
   * first intent that yields anything wins.
   *
   * @param {string[]} sources Cart product ids to ask about
   * @returns {Promise<Element[]>}
   */
  async fetchRecommendationPanels(sources) {
    for (const intent of INTENTS) {
      const panels = await Promise.all(sources.map((id) => this.fetchPanel(this.urlFor(id, intent))));
      const found = panels.filter(Boolean);
      if (this.mergeSlides(found).length > 0) return found;
    }

    return [];
  }

  async load() {
    if (this.loaded) return;

    const productIds = this.cartProductIds;
    if (!this.dataset.endpoint || productIds.length === 0) return;
    this.loaded = true;

    this.controller = new AbortController();

    /* No point asking more sources than the panel has slots: the merge takes
       at least one slide from each source before it takes a second from any,
       so sources beyond the limit could never place a product anyway. This is
       what keeps a twenty-line cart from firing twenty requests. */
    const sources = productIds.slice(0, this.limit);

    try {
      /* A cart with club products is answered from those clubs alone. Shopify's
         related products for a club item are the same item from other clubs,
         so they would only ever be filtered away. */
      const isClubCart = this.cartTeamHandles.size > 0 && this.teamCollectionUrls.length > 0;
      const panels = isClubCart ? await this.fetchTeamPanels() : await this.fetchRecommendationPanels(sources);

      const slides = this.mergeSlides(panels);
      if (slides.length === 0) return;

      /* The first reply supplies the panel — heading, carousel, the lot —
         and its track is refilled with the merged slides. Rebuilding that
         shell here would mean keeping a copy of the section's markup in
         JavaScript. The carousel counts its own slides (it is rendered
         `data-total-auto`), so a different number than the section rendered
         needs no telling. */
      const shell = panels[0];
      const track = shell.querySelector('[data-carousel-track]');
      if (!track) return;

      track.replaceChildren(...slides);
      this.innerHTML = shell.innerHTML;
    } catch (error) {
      /* An aborted fetch is the drawer closing mid-flight, not a failure. The
         panel is an upsell: if it cannot load, the cart still works. */
      if (error.name !== 'AbortError') this.loaded = false;
    }
  }
}

if (!customElements.get('cart-upsell')) {
  customElements.define('cart-upsell', CartUpsell);
}
